import React from 'react'
import { Grid, TextField, InputAdornment, IconButton } from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import {
  UseFormRegister,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue
} from 'react-hook-form'
import { validatePasswordComplexity } from 'utils/formatUtils'
import { useTranslation } from 'react-i18next'

interface PasswordFieldsProps {
  register: UseFormRegister<any>
  errors: FieldErrors<any>
  watch: UseFormWatch<any>
  setValue?: UseFormSetValue<any>
  showPassword: boolean
  showConfirmPassword: boolean
  toggleShowPassword: () => void
  toggleShowConfirmPassword: () => void
  validatePasswordComplexity?: (value: string) => string | null
}

const PasswordFields: React.FC<PasswordFieldsProps> = ({
  register,
  errors,
  watch,
  setValue,
  showPassword,
  showConfirmPassword,
  toggleShowPassword,
  toggleShowConfirmPassword
}) => {
  const handleTrimmedChange = (
    field: 'password' | 'confirmPassword',
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const trimmedValue = e.target.value.replace(/\s/g, '')
    if (setValue) {
      setValue(field, trimmedValue)
    }
  }
  const { t } = useTranslation()
  return (
    <>
      <Grid item xs={12}>
        <TextField
          label={t('passwordFields.newPassword')}
          variant='outlined'
          fullWidth
          type={showPassword ? 'text' : 'password'}
          {...register('password', {
            required: t('passwordFields.errors.passwordRequired'),
            validate: (value: string) => {
              const complexityError = validatePasswordComplexity(value)
              return complexityError || true
            }
          })}
          onChange={e =>
            handleTrimmedChange(
              'password',
              e as React.ChangeEvent<HTMLInputElement>
            )
          }
          required
          error={!!errors.password}
          helperText={
            errors.password?.message ? String(errors.password.message) : ''
          }
          inputProps={{ maxLength: 64 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={toggleShowPassword} edge='end'>
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <TextField
          label={t('passwordFields.confirmNewPassword')}
          variant='outlined'
          fullWidth
          type={showConfirmPassword ? 'text' : 'password'}
          {...register('confirmPassword', {
            required: t('passwordFields.errors.confirmRequired'),
            validate: (value: string) =>
              value === watch('password') ||
              t('passwordFields.errors.passwordMismatch')
          })}
          onChange={e =>
            handleTrimmedChange(
              'confirmPassword',
              e as React.ChangeEvent<HTMLInputElement>
            )
          }
          required
          error={!!errors.confirmPassword}
          helperText={
            typeof errors.confirmPassword?.message === 'string'
              ? errors.confirmPassword.message
              : ''
          }
          inputProps={{ maxLength: 64 }}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={toggleShowConfirmPassword} edge='end'>
                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Grid>
    </>
  )
}

export default PasswordFields
